import { Injectable } from '@angular/core';
import { showDefects, showReports } from '@frontend-site/main-header/domain';
import { CorrectionsMenuFactory } from '@frontend-site/main-header/domain';
import { ProjectStore } from '@frontend-site/shared/core';
import { ProjectInfo } from '@frontend-site/shared/data';
import { LocalStoragePersister } from '../../infrastructure/local-storage-persister';
import { PageId } from '../entities/last-page';

interface ProjectRememberMePageConfig {
    path(projectInfo: ProjectInfo): string;

    isAllowRemember(projectInfo: ProjectInfo): boolean;
}

const PERSISTENCE_KEY = 'project_last_page';

const CAN_REMEMBER_ROUTE_MAPPING = new Map<PageId, ProjectRememberMePageConfig>([
    [
        'execution',
        {
            path(projectInfo: ProjectInfo): string {
                return `/tm/${projectInfo.id}/execution`;
            },
            isAllowRemember(projectInfo): boolean {
                return !projectInfo.isRestrictedApp();
            }
        }
    ],
    [
        'project_dashboard',
        {
            path(projectInfo: ProjectInfo): string {
                return `/tm/${projectInfo.id}/project/dashboard`;
            },
            isAllowRemember(projectInfo): boolean {
                return !projectInfo.isRestrictedApp() && !projectInfo.qc;
            }
        }
    ],
    [
        'corrections_list',
        {
            path(projectInfo: ProjectInfo): string {
                return `/${projectInfo.id}/corrections/list`;
            },
            isAllowRemember(projectInfo: ProjectInfo): boolean {
                return !projectInfo.isRestrictedApp() && CorrectionsMenuFactory.showCodeCorrection(projectInfo);
            }
        }
    ],
    [
        'issues_list',
        {
            path(projectInfo: ProjectInfo): string {
                return `/${projectInfo.id}/issues/list`;
            },
            isAllowRemember(projectInfo: ProjectInfo): boolean {
                return (
                    !projectInfo.isRestrictedApp() &&
                    ((!projectInfo.hrOnly && (projectInfo.isUpgradeProject() || projectInfo.isSupportPackageProject())) ||
                        projectInfo.isS4Project())
                );
            }
        }
    ],
    [
        'development_list',
        {
            path(projectInfo: ProjectInfo): string {
                return `${projectInfo.id}/development/list`;
            },
            isAllowRemember(projectInfo: ProjectInfo): boolean {
                return (
                    projectInfo.rdxEnabled &&
                    !projectInfo.testCatalog &&
                    !projectInfo.qc &&
                    (projectInfo.hasModule('ANALYSIS_MANAGEMENT') || !projectInfo.modular)
                );
            }
        }
    ],
    [
        'requirements_list',
        {
            path(projectInfo: ProjectInfo): string {
                return `${projectInfo.id}/requirements/list`;
            },
            isAllowRemember(projectInfo: ProjectInfo): boolean {
                return projectInfo.rdxEnabled && !projectInfo.testCatalog && !projectInfo.qc;
            }
        }
    ],
    [
        'reports',
        {
            path(projectInfo: ProjectInfo): string {
                return `/tm/${projectInfo.id}/reports`;
            },
            isAllowRemember(projectInfo: ProjectInfo): boolean {
                return showReports(projectInfo);
            }
        }
    ],
    [
        'defects_list',
        {
            path(projectInfo: ProjectInfo): string {
                return `/${projectInfo.id}/defects/list`;
            },
            isAllowRemember(projectInfo: ProjectInfo): boolean {
                return showDefects(projectInfo);
            }
        }
    ],
    [
        'rdx_project_requirements_list',
        {
            path(projectInfo): string {
                return `/tm/${projectInfo.id}/releaseDynamix/${projectInfo.companyId}/project/requirements/list`;
            },
            isAllowRemember(projectInfo): boolean {
                return projectInfo.rdxEnabled && !projectInfo.isSalesforceProject();
            }
        }
    ],
    [
        'corrections_correctionPlan',
        {
            path(projectInfo): string {
                return `/tm/${projectInfo.id}/ia/corrections/correctionPlan`;
            },
            isAllowRemember(projectInfo): boolean {
                return !projectInfo.isRestrictedApp() && CorrectionsMenuFactory.showCodeCorrection(projectInfo);
            }
        }
    ]
]);

@Injectable({ providedIn: 'root' })
export class ProjectLastPageVisitedService {
    constructor(private readonly projectStore: ProjectStore, private readonly persister: LocalStoragePersister<PageId>) {}

    set(pageId: PageId): void {
        const projectInfo = this.getProjectInfo();
        if (!projectInfo) {
            return;
        }

        const rememberMePage = CAN_REMEMBER_ROUTE_MAPPING.get(pageId);
        if (!rememberMePage || !rememberMePage.isAllowRemember(projectInfo)) {
            return;
        }

        this.persister.set(PERSISTENCE_KEY, pageId);
    }

    get(): string | null {
        const projectInfo = this.getProjectInfo();
        if (!projectInfo) {
            return null;
        }

        const lastPageId = this.persister.get(PERSISTENCE_KEY);
        if (!lastPageId) {
            return null;
        }

        const config = CAN_REMEMBER_ROUTE_MAPPING.get(lastPageId);
        if (!config || !config.isAllowRemember(projectInfo)) {
            return null;
        }

        return config.path(projectInfo);
    }

    getProjectInfo(): ProjectInfo | null {
        return this.projectStore.projectDetails;
    }
}
